import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSchoolLanguageOptional } from '../../../contexts/SchoolLanguageContext';
import { fetchSchoolLanguage, type StudentSchoolLanguage } from '../../platform/api/schools';
import { useLearnerCurriculum } from '../../curriculum/hooks/useLearnerCurriculum';
import { isEffectiveTamilLearner } from '../../curriculum/learnerCurriculumOverride';
import {
  isMalayLanguageCode,
  isMalaySchoolLanguage,
  MALAY_TUTOR_TENANT_LABELS,
  resolveTutorTenantLabels,
  TAMIL_TUTOR_TENANT_LABELS,
  type TutorTenantLabels,
} from '../tutorTenantLabels';
import { getPublicTenantIntent } from '../../../utils/publicTenantHandoff';

type LoadState = 'idle' | 'loading' | 'ready' | 'error';

const TRANSLATABLE_LABEL_KEYS = [
  'moduleTitleAiTutor',
  'moduleTitleCommunicationTutor',
  'progressTitle',
  'practiceCta',
  'activityTitle',
  'emptyLessonsMessage',
  'progressEmptyHint',
  'keepPractisingHint',
  'skillsLoadingLabel',
  'openTutorLinkLabel',
  'positiveGuidance',
  'recommendationEmptyHint',
] as const;

/**
 * Loads trusted school language settings and resolves tutor UI labels.
 * Prefers SchoolLanguageContext when mounted inside the portal provider.
 */
export function useTutorTenantLabels(): {
  labels: TutorTenantLabels;
  loading: boolean;
  schoolLanguage: StudentSchoolLanguage | null;
} {
  const { t, i18n } = useTranslation();
  const { curriculum } = useLearnerCurriculum();
  const schoolLangCtx = useSchoolLanguageOptional();
  const hasPortalContext = schoolLangCtx != null;
  const [fetchedLanguage, setFetchedLanguage] = useState<StudentSchoolLanguage | null>(null);
  const [loadState, setLoadState] = useState<LoadState>(() =>
    schoolLangCtx?.schoolLanguageSettings ? 'ready' : 'loading',
  );

  const contextSettings = schoolLangCtx?.schoolLanguageSettings ?? null;
  const contextLoading = schoolLangCtx?.isLoading === true;

  useEffect(() => {
    if (contextSettings) {
      setFetchedLanguage(contextSettings);
      setLoadState('ready');
      return;
    }
    if (hasPortalContext && contextLoading) {
      setLoadState('loading');
      return;
    }

    let cancelled = false;
    setLoadState('loading');
    void fetchSchoolLanguage()
      .then((lang) => {
        if (cancelled) return;
        setFetchedLanguage(lang);
        setLoadState('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setFetchedLanguage(null);
        setLoadState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [contextSettings, contextLoading, hasPortalContext]);

  const schoolLanguage = contextSettings ?? fetchedLanguage;

  const labels = useMemo(() => {
    const preAuthHint = getPublicTenantIntent();
    const isMalayFallback =
      isMalayLanguageCode(i18n.language) ||
      Boolean(preAuthHint?.isMalay);
    const legacyTenantIsMalay = schoolLanguage
      ? isMalaySchoolLanguage(schoolLanguage)
      : isMalayFallback;

    // Tamil-track learners use Tamil tutor labels regardless of UI locale.
    if (isEffectiveTamilLearner({ curriculum, legacyTenantIsMalay })) {
      const base = TAMIL_TUTOR_TENANT_LABELS;
      const ns = 'dashboard.tutor.tamil';
      const out: TutorTenantLabels = { ...base };
      for (const key of TRANSLATABLE_LABEL_KEYS) {
        out[key] = t(`${ns}.${key}`, { defaultValue: base[key] });
      }
      return out;
    }

    const uiFallback = isMalayFallback
      ? MALAY_TUTOR_TENANT_LABELS
      : TAMIL_TUTOR_TENANT_LABELS;

    const base = schoolLanguage
      ? resolveTutorTenantLabels(schoolLanguage)
      : uiFallback;

    const ns = base.isMalay
      ? 'dashboard.tutor.malay'
      : 'dashboard.tutor.tamil';

    const out: TutorTenantLabels = { ...base };

    for (const key of TRANSLATABLE_LABEL_KEYS) {
      out[key] = t(`${ns}.${key}`, {
        defaultValue: base[key],
      });
    }

    return out;
  }, [schoolLanguage, curriculum, t, i18n.language]);

  return {
    labels,
    loading:
      !schoolLanguage &&
      (loadState === 'loading' ||
        loadState === 'idle' ||
        (Boolean(schoolLangCtx) && contextLoading)),
    schoolLanguage,
  };
}
